import { KeyboardEventHandler, useCallback, useMemo, useRef } from 'react';

import {
  useCombobox,
  UseComboboxState,
  UseComboboxStateChange,
  UseComboboxStateChangeOptions,
  useMultipleSelection,
  UseMultipleSelectionProps,
} from 'downshift';

import { useForwardedRef } from '@adsk/alloy-react-helpers';

import useAutocomplete from './useAutocomplete';
import useDropdownId from './useDropdownId';
import useKeepMenuOpenOnBlur from './useKeepMenuOpenOnBlur';
import {
  checkReferenceEquality,
  defaultIsItemDisabled,
  defaultLabelGetter,
  createDefaultGetItemKey,
  deduplicateItems,
  includesItem,
  isGroupHeader,
} from '../helpers';
import { flattenGroupItemsToDownshift } from '../helpers/groupedItems';
import {
  getMultipleSelectionHandlers,
  handleMultiItemSelect,
} from '../helpers/sharedMultipleSelection';
import {
  GenericItem,
  GetInputProps,
  ItemsOrGroupedItems,
  UseMultiAutocompleteProps,
} from '../types';

/**
 * A hook used to manage all logic needed for a multiple select Autocomplete (searchable) dropdown.
 */
const useMultiAutocomplete = <
  TItem extends GenericItem,
  TTarget extends HTMLElement = HTMLDivElement,
>({
  id,
  items,
  groupedItems,
  selectedItems: selectedItemsProp,
  itemToString = defaultLabelGetter,
  multipleSelectionStateReducer,
  stateReducer: stateReducerProp,
  target: targetRefProp,
  overlayRef: overlayRefProp,
  inputRef: inputRefProp,
  isItemDisabled = defaultIsItemDisabled,
  onStateChange: onStateChangeProp,
  onItemSelect: onItemSelectProp,
  getItemKey: getItemKeyProp,
  ...props
}: UseMultiAutocompleteProps<TItem, TTarget>) => {
  const dropdownId = useDropdownId(id);
  const maybeGroupedItems = useMemo(
    () => items ?? groupedItems ?? [],
    [groupedItems, items],
  );

  const getItemKey = useMemo(
    () => getItemKeyProp || createDefaultGetItemKey(itemToString),
    [getItemKeyProp, itemToString],
  );

  checkReferenceEquality({
    items: maybeGroupedItems,
    ...props,
    selectedItems: selectedItemsProp,
  });

  const inputRef = useForwardedRef(inputRefProp ?? null);
  const targetRef = useForwardedRef(targetRefProp ?? null);
  const overlayRef = useForwardedRef(overlayRefProp ?? null);

  const useMultipleSelectionArgs: UseMultipleSelectionProps<TItem> = {};
  if (selectedItemsProp) {
    useMultipleSelectionArgs.selectedItems = selectedItemsProp;
  }
  if (multipleSelectionStateReducer) {
    useMultipleSelectionArgs.stateReducer = multipleSelectionStateReducer;
  }

  const {
    getDropdownProps,
    addSelectedItem: rawAddSelectedItem,
    removeSelectedItem: rawRemoveSelectedItem,
    getSelectedItemProps: rawGetSelectedItemProps,
    selectedItems: rawSelectedItems,
    ...useMultipleSelectionReturnValue
  } = useMultipleSelection<TItem>({
    ...useMultipleSelectionArgs,
    initialActiveIndex: 0,
    ...props,
  });

  const selectedItems = useMemo(
    () =>
      getItemKeyProp
        ? deduplicateItems(rawSelectedItems, getItemKey)
        : rawSelectedItems,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getItemKeyProp only gates the branch; getItemKey already changes with it
    [rawSelectedItems, getItemKey],
  );

  const flatItems = useMemo(
    () => flattenGroupItemsToDownshift(maybeGroupedItems),
    [maybeGroupedItems],
  );

  const _isItemDisabled = useCallback(
    (item: TItem) => isItemDisabled(item, flatItems.indexOf(item)),
    [flatItems, isItemDisabled],
  );

  const itemWasRemoved = useRef(false);

  const addSelectedItem = useCallback(
    (item: TItem) => {
      if (_isItemDisabled(item)) {
        return;
      }

      if (!includesItem(rawSelectedItems, item, getItemKey)) {
        rawAddSelectedItem(item);
      }
    },
    [_isItemDisabled, rawAddSelectedItem, rawSelectedItems, getItemKey],
  );

  const removeSelectedItem = useCallback(
    (item: TItem) => {
      if (_isItemDisabled(item)) {
        return;
      }

      itemWasRemoved.current = true;

      rawRemoveSelectedItem(item);
    },
    [_isItemDisabled, rawRemoveSelectedItem],
  );

  const { onKeyDown: controlOnKeyDown, getShouldKeepMenuOpenOnBlur } =
    useKeepMenuOpenOnBlur({ id: dropdownId });

  const autocompleteStateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      { changes, type }: UseComboboxStateChangeOptions<TItem>,
    ): Partial<UseComboboxState<TItem>> => {
      if (type !== useCombobox.stateChangeTypes.InputBlur) {
        itemWasRemoved.current = false;
      }

      switch (type) {
        case useCombobox.stateChangeTypes.InputClick:
          if (state.isOpen) {
            return {
              ...changes,
              highlightedIndex: state.highlightedIndex,
              isOpen: true,
            };
          }

          return changes;
        case useCombobox.stateChangeTypes.InputBlur: {
          const overrides: Partial<UseComboboxState<TItem>> = {
            isOpen: getShouldKeepMenuOpenOnBlur(),
            inputValue: state.inputValue,
          };
          if (itemWasRemoved.current) {
            overrides.isOpen = true;
          }

          return {
            ...changes,
            ...overrides,
          };
        }
        case useCombobox.stateChangeTypes.InputKeyDownEscape:
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.ToggleButtonClick:
          return {
            ...changes,
            inputValue: '',
          };
        case useCombobox.stateChangeTypes.ControlledPropUpdatedSelectedItem:
          if (state.inputValue && state.inputValue.length > 0) {
            return {
              ...changes,
              inputValue: state.inputValue,
            };
          }
          return {
            ...changes,
            inputValue: '',
          };
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          const overrides: Partial<UseComboboxState<TItem>> = {
            isOpen: true,
            highlightedIndex: state.highlightedIndex,
          };
          if (changes.selectedItem) {
            overrides.inputValue = '';

            if (items) {
              overrides.highlightedIndex = items.indexOf(changes.selectedItem);
            }
          }

          return {
            ...changes,
            ...overrides,
          };
        }
        default:
          return changes;
      }
    },
    [getShouldKeepMenuOpenOnBlur, items],
  );

  const onStateChange = useCallback(
    ({ type, selectedItem }: UseComboboxStateChange<TItem>) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick: {
          if (
            selectedItem !== null &&
            selectedItem !== undefined &&
            !isGroupHeader(selectedItem)
          ) {
            handleMultiItemSelect({
              item: selectedItem,
              selectedItems,
              addSelectedItem,
              removeSelectedItem,
              getItemKey,
              onItemSelect: onItemSelectProp,
            });
          }
          break;
        }
        default:
          break;
      }

      onStateChangeProp?.({ type, selectedItem });
    },
    [
      addSelectedItem,
      onItemSelectProp,
      onStateChangeProp,
      removeSelectedItem,
      selectedItems,
      getItemKey,
    ],
  );

  const stateReducer = useCallback(
    (
      state: UseComboboxState<TItem>,
      actionAndChanges: UseComboboxStateChangeOptions<TItem>,
    ) => {
      const updatedState = autocompleteStateReducer(state, actionAndChanges);
      const customUpdatedState = stateReducerProp?.(state, actionAndChanges);
      return {
        ...actionAndChanges.changes,
        ...updatedState,
        ...customUpdatedState,
      };
    },
    [autocompleteStateReducer, stateReducerProp],
  );

  const itemArgs = { items, groupedItems } as ItemsOrGroupedItems<TItem>;

  const {
    controlProps: {
      isOpen,
      getInputProps: rawGetInputProps,
      selectedItem: _ignored1,
      ...controlProps
    },
    menuProps: {
      getItemProps: rawGetItemProps,
      selectedItem: _ignored2,
      ...menuProps
    },
    ...useAutocompleteReturnValue
  } = useAutocomplete({
    id: dropdownId,
    ...itemArgs,
    itemToString,
    selectedItem: null,
    inputRef,
    target: targetRef,
    overlayRef,
    stateReducer,
    onStateChange,
    isItemDisabled,
    getItemKey,
    ...props,
  });

  const getInputProps = useCallback<GetInputProps>(
    ({ onKeyDown: onKeyDownProp, ...options } = {}) => {
      const dropdownProps = getDropdownProps({
        preventKeyAction: isOpen,
      });

      const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
        controlOnKeyDown(e);

        onKeyDownProp?.(e);
      };

      return rawGetInputProps({
        ...dropdownProps,
        onKeyDown,
        ...options,
      });
    },
    [controlOnKeyDown, getDropdownProps, isOpen, rawGetInputProps],
  );

  const multipleSelectionHandlers = useMemo(
    () =>
      getMultipleSelectionHandlers({
        id: dropdownId,
        selectedItems,
        setSelectedItems: useMultipleSelectionReturnValue.setSelectedItems,
        isItemDisabled: _isItemDisabled,
        closeMenu: useAutocompleteReturnValue.closeMenu,
        rawGetItemProps,
        rawGetSelectedItemProps,
        refToFocusOnClear: inputRef,
        getItemKey,
      }),
    [
      dropdownId,
      inputRef,
      _isItemDisabled,
      rawGetItemProps,
      rawGetSelectedItemProps,
      selectedItems,
      useAutocompleteReturnValue.closeMenu,
      useMultipleSelectionReturnValue.setSelectedItems,
      getItemKey,
    ],
  );

  return {
    controlProps: {
      ...controlProps,
      isOpen,
      getInputProps,
      selectedItems,
      removeSelectedItem,
      ...multipleSelectionHandlers.controlProps,
    },
    menuProps: {
      ...menuProps,
      selectedItems,
      addSelectedItem,
      removeSelectedItem,
      ...multipleSelectionHandlers.menuProps,
    },
    ...useMultipleSelectionReturnValue,
    ...useAutocompleteReturnValue,
  };
};

export default Object.assign(useMultiAutocomplete, {
  stateChangeTypes: useCombobox.stateChangeTypes,
  multipleSelectionStateChangeTypes: useMultipleSelection.stateChangeTypes,
});
